import { Scores } from "../Scores";
import {
  MatchSet,
  getMatchSetDriverTimes,
  matchSetHasData,
  matchSetHasDriverData,
} from "./Set/MatchSet";
import { Positions } from "../Positions";
import { MatchFormat } from "./MatchFormat";
import { getResults } from "model/Results";
import { nonUndefined, onlyUnique } from "Utility";
import {
  getRosterDriverIds,
  getDefinedRosterIds,
  getRoster,
  Roster,
  BYE,
  isForfeited,
  getRosterIdOrPlaceholder,
} from "model/Roster/Roster";
import { MatchTeams } from "./MatchTeams";
import { RosterScope, SQUAD_SCOPE } from "model/Roster/RosterScope";

export type Match = {
  id: string;
  format: MatchFormat;
  isComplete: boolean;
  isDelayed: boolean;
  numWinners: number;
  finalPositions: Positions | undefined;
  scores: Scores | undefined;
  sets: MatchSet[];
  startTimeMs: number;
  teams: MatchTeams;
};

export const getMatchRosterOrPlaceholderIds = (match: Match) =>
  match.teams.rosters.map((r) => getRosterIdOrPlaceholder(r) ?? []);

export const getMatchDriverIds = (match: Match) =>
  match.teams.rosters.flatMap(
    (r) => getRosterDriverIds(r, match.teams.scope) ?? []
  );

export const matchContainsRoster = (
  match: Match,
  rosterId: string,
  scope?: RosterScope
) => {
  if (!!scope && match.teams.scope !== scope) {
    return false;
  }

  return getDefinedRosterIds(match.teams.rosters).includes(rosterId);
};

export const matchContainsTeam = (match: Match, teamId: string) => {
  if (match.teams.scope !== SQUAD_SCOPE) {
    return false;
  }

  return match.teams.rosters.find(
    (r) => typeof r !== "string" && r.teamId === teamId
  );
};

export const matchContainsDriver = (match: Match, driverId: string) =>
  getMatchDriverIds(match).includes(driverId);

export const matchHasData = (match: Match) => {
  if (!!match.scores && Object.values(match.scores).some((s) => !!s)) {
    return true;
  }

  return match.sets.some(matchSetHasData);
};

export const matchHasDriverData = (match: Match) => {
  return match.sets.some(matchSetHasDriverData);
};

export const getMatchResults = (match: Match) => {
  if (!match.isComplete) {
    return undefined;
  }

  return getResults(match.finalPositions, match.numWinners);
};

export const getWinningRosterIds = (match: Match) => {
  const results = getMatchResults(match);
  if (!results) {
    return undefined;
  }

  return Object.keys(results).filter((id) => results[id] === "W");
};

export const getMatchPickSequences = (match: Match) => {
  return match.sets.flatMap((s) => s.picks);
};

export const getMatchDriverTimes = (
  match: Match,
  includeTimeAttack: boolean,
  trackId?: string,
  rosterId?: string
) => {
  let driverIds: string[] | undefined;
  if (!!rosterId) {
    const roster = getRoster(match.teams.rosters, rosterId);
    if (!roster) {
      return [];
    }

    driverIds = getRosterDriverIds(roster, match.teams.scope) ?? [];
  }

  const sets = includeTimeAttack
    ? match.sets
    : match.sets.filter((s) => s.type !== "TimeAttack");
  return sets.flatMap((s) => getMatchSetDriverTimes(s, trackId, driverIds));
};

export const matchCompleteByByesOrForfeits = (rosters: Roster[]) => {
  if (rosters.length === 0) {
    return false;
  }

  return rosters.filter((r) => r !== BYE && !isForfeited(r)).length <= 1;
};

export const matchCompleteByByes = (rosters: Roster[]) => {
  if (rosters.length === 0) {
    return false;
  }

  return rosters.filter((r) => r !== BYE).length <= 1;
};

export const getMatchPlayedTrackIds = (match: Match) => {
  return nonUndefined(
    match.sets.flatMap((s) => s.tracks.flatMap((t) => t.trackId))
  ).filter(onlyUnique);
};
